'use client'

import { memo } from 'react'
import Link from 'next/link'
import { Ninxl8BitFrame } from '@/app/(marketing)/_components/8bit'
import { NIN_MCP_SERVER_URL, NIN_WEBUPI_DOCS_URL } from './nin-org-links'
import { NinDeviceMock } from './nin-device-mock'

export type NinProductCard = {
  id: string
  name: string
  audience: string
  description: string
  /** Live product landing or external docs URL */
  href: string
  badge?: string
  recommended?: boolean
  /** Open in a new tab (external docs) */
  external?: boolean
  /** Half-visible device chrome on the right */
  device?: 'laptop' | 'phone'
}

export type NinSecondaryProduct = {
  id: string
  name: string
  blurb: string
  href?: string
  external?: boolean
}

// baby: Visible projects. NINX (/ninxl), NINK (/nink), Ningenie
export const NIN_PRODUCTS: NinProductCard[] = [
  {
    id: 'ninxl',
    name: 'NINX',
    audience: 'Local-first desktop',
    description:
      'Private AI on your Mac or PC. Chat, voice, tools, and automations stay on-device by default.',
    href: '/ninxl',
    badge: 'RECOMMENDED',
    recommended: true,
    device: 'laptop',
  },
  {
    id: 'nink',
    name: 'NINK',
    audience: 'Tray voice writing',
    description:
      'Ink Flow dictation from the menu bar. Speak in any app. Whisper and rewrite stay local by default.',
    href: '/nink',
    badge: 'DICTATION',
    device: 'laptop',
  },
  {
    id: 'ningenie',
    name: 'NINGENIE',
    audience: 'Mobile',
    description:
      "Digital assistant that executes tasks across your apps. Don't ask, just task.",
    href: '/home',
    badge: 'MOBILE',
    device: 'phone',
  },
]

// baby: Low-focus chips under the main cards
export const NIN_SECONDARY_PRODUCTS: NinSecondaryProduct[] = [
  {
    id: 'mcp',
    name: 'MCP',
    blurb: 'Tool servers',
    href: NIN_MCP_SERVER_URL,
    external: true,
  },
  {
    id: 'protocols',
    name: 'Protocols',
    blurb: 'WebUPI',
    href: NIN_WEBUPI_DOCS_URL,
    external: true,
  },
]

const ProductCard = memo(function ProductCard({ product }: { product: NinProductCard }) {
  const body = (
    <Ninxl8BitFrame
      label={product.badge ?? 'LIVE'}
      className="group relative h-full transition hover:translate-x-[1px] hover:translate-y-[1px]"
    >
      {/* baby: Clip device only: keep frame overflow visible so badge sits on the border */}
      {product.device ? (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <NinDeviceMock kind={product.device} />
        </div>
      ) : null}
      <div className="relative z-[1] flex h-full min-h-[11rem] flex-col gap-3 pr-16 sm:pr-20 md:pr-24">
        <div className="ninxl-8bit-label ninxl-theme-text-accent tracking-[0.15em]">
          {product.audience.toUpperCase()}
        </div>
        <h3 className="ninxl-8bit-heading ninxl-theme-text text-sm md:text-base">{product.name}</h3>
        <p className="ninxl-8bit-body flex-1 text-left text-base md:text-lg">{product.description}</p>
        <span className="nin-clean-cta ninxl-pixel-btn ninxl-pixel-btn-primary mt-2 inline-flex w-fit">
          {product.external ? 'Docs' : 'Open'}
        </span>
      </div>
    </Ninxl8BitFrame>
  )

  if (product.external) {
    return (
      <a
        href={product.href}
        target="_blank"
        rel="noopener noreferrer"
        className="nin-product-card block no-underline"
        data-product={product.id}
        data-status="live"
        aria-label={`Open ${product.name} docs`}
      >
        {body}
      </a>
    )
  }

  return (
    <Link
      href={product.href}
      className="nin-product-card block no-underline"
      data-product={product.id}
      data-status="live"
      aria-label={`Open ${product.name}`}
    >
      {body}
    </Link>
  )
})

const SecondaryChip = memo(function SecondaryChip({
  product,
}: {
  product: NinSecondaryProduct
}) {
  const className =
    'ninxl-pixel-border ninxl-theme-bg-frame ninxl-theme-text inline-flex items-center gap-2 px-3 py-1.5 opacity-55 transition hover:translate-x-[1px] hover:translate-y-[1px] hover:opacity-80'

  const inner = (
    <>
      <span className="ninxl-8bit-label tracking-[0.12em]">{product.name}</span>
      <span className="ninxl-theme-text-muted text-[10px] font-mono md:text-xs">{product.blurb}</span>
    </>
  )

  if (product.href) {
    if (product.external) {
      return (
        <a
          href={product.href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          aria-label={`${product.name}: ${product.blurb}`}
        >
          {inner}
        </a>
      )
    }
    return (
      <Link
        href={product.href}
        className={className}
        aria-label={`${product.name}: ${product.blurb}`}
      >
        {inner}
      </Link>
    )
  }

  return (
    <span
      className={`${className} cursor-default`}
      aria-label={`${product.name}: ${product.blurb}`}
    >
      {inner}
    </span>
  )
})

export const NinProductGrid = memo(function NinProductGrid({
  products = NIN_PRODUCTS,
  secondary = NIN_SECONDARY_PRODUCTS,
}: {
  products?: NinProductCard[]
  secondary?: NinSecondaryProduct[]
}) {
  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-5 pt-3">
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>

      {secondary.length > 0 ? (
        <div className="flex flex-col items-center gap-2">
          <span className="ninxl-8bit-label ninxl-theme-text-muted tracking-[0.14em] opacity-45">
            ALSO
          </span>
          <div className="flex flex-nowrap items-center justify-center gap-2 sm:gap-3">
            {secondary.map((product) => (
              <SecondaryChip key={product.id} product={product} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
})



